// PROTOTYPE — draggable brick palette (LEFT sidebar), grouped into sections:
// Triggers / Data processing / Actions. Names copied from the current automation
// UI. Unavailable bricks show a "בקרוב" badge and can't be added (matches today).
//
// Drag a brick onto the canvas (HTML5 drag API + dataTransfer) or click to add.

import {useState} from "react";
import {Box, Chip, Collapse, Typography} from "@mui/material";
import {
    ExpandMore as ExpandIcon,
    BoltOutlined as BoltIcon,
    PersonAddAlt1Outlined as CreateIcon,
    EditOutlined as UpdateIcon,
    PersonRemoveOutlined as DeleteTraineeIcon,
    EmailOutlined as EmailIcon,
    SmsOutlined as SmsIcon,
    HttpOutlined as HttpIcon,
    NotificationsActiveOutlined as PushIcon,
    WhatsApp as WhatsAppIcon,
    TuneOutlined as DataIcon,
    FilterAltOutlined as FilterIcon,
    PersonOutline as UserIcon,
    FitnessCenterOutlined as WorkoutIcon,
    MonitorWeightOutlined as WeightIcon,
    AssignmentOutlined as FormIcon,
    RestaurantMenuOutlined as NutritionIcon,
    DragIndicator as DragIcon,
} from "@mui/icons-material";
import {RTL} from "../../../cache/CacheRtl";
import {borders, colors} from "../../ui/colors";
import {PALETTE_SECTIONS} from "./nodeCatalog";
import {availableActionKinds, availableDataKinds} from "./catalogStore";
import arboxLogo from "../../../assets/images/arbox.webp";

// Brand teal — matches the "לקוח חדש" CTA and the canvas nodes.
const BRAND = "#01BBA7";
const BRAND_TINT = "rgba(1,187,167,0.08)";

// The dataTransfer payload is "nodeType:kind[:eventType]" so the canvas can build
// the right node on drop.
export const DRAG_MIME = "application/x-progress-brick";

const ICON = {
    // actions
    CREATE_TRAINEE: CreateIcon, UPDATE_TRAINEE: UpdateIcon, DELETE_TRAINEE: DeleteTraineeIcon,
    ASSIGN_PROGRAM_TEMPLATE: WorkoutIcon, ASSIGN_NUTRITION_MENU: NutritionIcon, SEND_FORM: FormIcon,
    EMAIL: EmailIcon, SMS: SmsIcon, HTTP: HttpIcon, PUSH: PushIcon, WHATSAPP: WhatsAppIcon,
    // data
    DATA_TRANSFORM: DataIcon, FILTER: FilterIcon,
    // triggers
    ARBOX_NEW_CLIENT: BoltIcon,
    USER_CREATED: UserIcon, USER_UPDATED: UserIcon, USER_DELETED: UserIcon,
    WORKOUT_COMPLETED: WorkoutIcon, WEIGHT_UPDATED: WeightIcon, FORM_COMPLETED: FormIcon,
};

const iconFor = (item) => ICON[item.eventType] ?? ICON[item.kind] ?? BoltIcon;

// Stable identity for a palette item (internal events share a kind).
const itemToken = (nodeType, item) =>
    item.kind === "INTERNAL_EVENT" ? `${nodeType}:${item.kind}:${item.eventType}` : `${nodeType}:${item.kind}`;

export default function BrickPalette() {
    const [collapsed, setCollapsed] = useState(() => new Set());
    const toggle = (id) => setCollapsed(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    const onDragStart = (e, token, available) => {
        if (!available) { e.preventDefault(); return; }
        e.dataTransfer.setData(DRAG_MIME, token);
        e.dataTransfer.effectAllowed = "move";
    };

    const renderItem = (nodeType, item) => {
        const Icon = iconFor(item);
        const token = itemToken(nodeType, item);
        // Available = the static catalog allows it AND the server registered its kind. The server
        // set is null until /bricks loads → fall back to the static flag (don't hide everything).
        const serverKinds = nodeType === "data" ? availableDataKinds() : availableActionKinds();
        const serverHasIt = serverKinds == null || serverKinds.has(item.kind);
        const available = item.available !== false && serverHasIt;
        const isArbox = item.source === "ARBOX";
        return (
            <Box
                key={token}
                draggable={available}
                onDragStart={e => onDragStart(e, token, available)}
                title={item.description}
                sx={{
                    position: "relative",
                    display: "flex", alignItems: "flex-start", gap: 1,
                    px: 1.25, py: 1,
                    border: borders.default, borderRadius: borders.radius.md,
                    bgcolor: colors.white,
                    cursor: available ? "grab" : "not-allowed",
                    opacity: available ? 1 : 0.55,
                    userSelect: "none",
                    transition: "all 0.15s",
                    "&:hover": available
                        ? {borderColor: BRAND, bgcolor: BRAND_TINT, transform: "translateX(-2px)"}
                        : undefined,
                    "&:active": available ? {cursor: "grabbing"} : undefined,
                }}
            >
                {isArbox
                    ? <Box component="img" src={arboxLogo} alt="Arbox"
                           sx={{width: 20, height: 20, borderRadius: "5px", mt: "1px", flexShrink: 0}}/>
                    : <Icon sx={{fontSize: 20, color: available ? BRAND : colors.textMuted, mt: "1px"}}/>}
                <Box sx={{flex: 1, minWidth: 0}}>
                    <Typography sx={{fontSize: "13px", fontWeight: 600, color: colors.textPrimary}}>
                        {item.label}
                    </Typography>
                    <Typography sx={{fontSize: "11px", color: colors.textMuted, lineHeight: 1.3}}>
                        {item.description}
                    </Typography>
                </Box>
                {available
                    ? <DragIcon sx={{fontSize: 16, color: colors.border, mt: "2px"}}/>
                    : <Chip label="בקרוב" size="small" sx={{
                        height: 18, fontSize: "9px", fontWeight: 700, bgcolor: colors.warning, color: colors.white,
                        "& .MuiChip-label": {px: 0.75},
                      }}/>}
            </Box>
        );
    };

    return (
        <RTL>
            <Box dir="rtl" sx={{
                width: 250, flexShrink: 0, height: "100%",
                bgcolor: colors.bg, borderLeft: borders.default,
                display: "flex", flexDirection: "column", overflowY: "auto",
            }}>
                <Box sx={{px: 2, py: 1.5, borderBottom: borders.default, bgcolor: colors.white, position: "sticky", top: 0, zIndex: 1}}>
                    <Typography sx={{fontSize: "14px", fontWeight: 700, color: colors.textPrimary}}>
                        רכיבים
                    </Typography>
                    <Typography sx={{fontSize: "11px", color: colors.textMuted, mt: 0.25}}>
                        גרור אל הקנבס כדי להוסיף
                    </Typography>
                </Box>
                <Box sx={{p: 1.5, display: "flex", flexDirection: "column", gap: 1.5}}>
                    {PALETTE_SECTIONS.map(section => {
                        const isOpen = !collapsed.has(section.id);
                        return (
                            <Box key={section.id}>
                                <Box
                                    onClick={() => toggle(section.id)}
                                    sx={{
                                        display: "flex", alignItems: "center", justifyContent: "space-between",
                                        cursor: "pointer", px: 0.5, py: 0.5, mb: 0.5,
                                        borderRadius: borders.radius.sm,
                                        "&:hover": {bgcolor: colors.white},
                                    }}
                                >
                                    <Typography sx={{
                                        fontSize: "11px", fontWeight: 700, color: colors.textMuted, letterSpacing: "0.3px",
                                    }}>
                                        {section.label} <Box component="span" sx={{color: colors.border}}>· {section.items.length}</Box>
                                    </Typography>
                                    <ExpandIcon sx={{
                                        fontSize: 18, color: colors.textMuted,
                                        transition: "transform 0.2s", transform: isOpen ? "none" : "rotate(-90deg)",
                                    }}/>
                                </Box>
                                <Collapse in={isOpen} unmountOnExit>
                                    <Box sx={{display: "flex", flexDirection: "column", gap: 1}}>
                                        {section.items.map(item => renderItem(section.nodeType, item))}
                                    </Box>
                                </Collapse>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </RTL>
    );
}
